import api from './api';

export const emailService = {
  getAllEmails: async (params = {}) => {
    try {
      const response = await api.get('/emails', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting email records:', error);
      throw error;
    }
  },

  getEmailById: async (id) => {
    try {
      const response = await api.get(`/emails/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error getting email record:', error);
      throw error;
    }
  },

  createEmail: async (emailData) => {
    try {
      const response = await api.post('/emails', emailData);
      return response.data;
    } catch (error) {
      console.error('Error creating email record:', error);
      throw error;
    }
  },

  updateEmail: async (id, emailData) => {
    try {
      const response = await api.put(`/emails/${id}`, emailData);
      return response.data;
    } catch (error) {
      console.error('Error updating email record:', error);
      throw error;
    }
  },

  deleteEmail: async (id) => {
    try {
      const response = await api.delete(`/emails/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting email record:', error);
      throw error;
    }
  },

  bulkDeleteEmails: async (emailIds) => {
    try {
      const response = await api.delete('/emails/bulk', {
        data: { emailIds }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting email records:', error);
      throw error;
    }
  },

  getEmailStats: async (startDate, endDate) => {
    try {
      const response = await api.get('/emails/stats', { 
        params: { startDate, endDate } 
      });
      return response.data;
    } catch (error) {
      console.error('Error getting email stats:', error);
      throw error;
    }
  },

  getEmailsByStatus: async (status) => {
    try {
      const response = await api.get(`/emails/status/${status}`);
      return response.data;
    } catch (error) {
      console.error('Error getting emails by status:', error);
      throw error;
    }
  },

  getEmailsByType: async (type) => {
    try {
      const response = await api.get(`/emails/type/${type}`);
      return response.data;
    } catch (error) {
      console.error('Error getting emails by type:', error);
      throw error;
    }
  },

  getEmailsByOwner: async (ownerId) => {
    try {
      const response = await api.get(`/emails/owner/${ownerId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting emails by owner:', error);
      throw error;
    }
  },

  getEmailThread: async (threadId) => {
    try {
      const response = await api.get(`/emails/thread/${threadId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting email thread:', error);
      throw error;
    }
  },

  getScheduledEmails: async (startDate, endDate) => {
    try {
      const response = await api.get('/emails/scheduled', { 
        params: { startDate, endDate } 
      });
      return response.data;
    } catch (error) {
      console.error('Error getting scheduled emails:', error);
      throw error;
    }
  },

  getEmailAnalytics: async (startDate, endDate) => {
    try {
      const response = await api.get('/emails/analytics', { 
        params: { startDate, endDate } 
      });
      return response.data;
    } catch (error) {
      console.error('Error getting email analytics:', error);
      throw error;
    }
  },

  sendEmail: async (id) => {
    try {
      const response = await api.post(`/emails/${id}/send`);
      return response.data;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  },

  // Email composition helpers
  createEmailTemplate: (templateData) => {
    return {
      subject: templateData.subject || '',
      emailBody: templateData.body || '',
      emailBodyHtml: templateData.htmlBody || '',
      isHtml: templateData.isHtml || false,
      templateName: templateData.templateName || '',
      templateVariables: templateData.variables || {}
    };
  },

  // Email validation helpers
  validateEmailAddress: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  validateEmailData: (emailData) => {
    const errors = [];
    
    if (!emailData.subject || emailData.subject.trim() === '') {
      errors.push('Subject is required');
    }
    
    if (!emailData.emailBody || emailData.emailBody.trim() === '') {
      errors.push('Email body is required');
    }
    
    if (!emailData.fromEmail || !emailService.validateEmailAddress(emailData.fromEmail)) {
      errors.push('Valid from email is required');
    }
    
    if (!emailData.toEmails || emailData.toEmails.length === 0) {
      errors.push('At least one recipient is required');
    } else {
      emailData.toEmails.forEach((recipient, index) => {
        if (!emailService.validateEmailAddress(recipient.email)) {
          errors.push(`Invalid email address at position ${index + 1}`);
        }
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Email formatting helpers
  formatEmailRecipients: (recipients) => {
    return recipients.map(recipient => ({
      email: recipient.email,
      name: recipient.name || recipient.email,
      type: recipient.type || 'To'
    }));
  },

  formatEmailForDisplay: (email) => {
    return {
      ...email,
      formattedDate: new Date(email.emailDate).toLocaleDateString(),
      formattedTime: new Date(email.emailDate).toLocaleTimeString(),
      recipientCount: email.toEmails.length + (email.ccEmails?.length || 0) + (email.bccEmails?.length || 0),
      hasAttachments: email.attachments && email.attachments.length > 0,
      isScheduled: email.isScheduled && email.emailStatus === 'Scheduled',
      isThread: email.threadCount > 1
    };
  },

  // Email search helpers
  searchEmails: async (searchTerm, filters = {}) => {
    try {
      const params = {
        search: searchTerm,
        ...filters
      };
      const response = await api.get('/emails', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching emails:', error);
      throw error;
    }
  },

  // Email filtering helpers
  getEmailFilters: () => {
    return {
      status: ['Draft', 'Sent', 'Delivered', 'Opened', 'Replied', 'Bounced', 'Failed', 'Cancelled'],
      type: ['Outbound', 'Inbound', 'Follow-up', 'Marketing', 'Support', 'Internal', 'Automated'],
      priority: ['High', 'Medium', 'Low'],
      category: ['Sales', 'Marketing', 'Support', 'Internal', 'Follow-up', 'Proposal', 'Demo', 'Contract', 'Other']
    };
  },

  // Email analytics helpers
  calculateEmailMetrics: (emails) => {
    const totalEmails = emails.length;
    const sentEmails = emails.filter(email => email.emailStatus === 'Sent').length;
    const deliveredEmails = emails.filter(email => email.emailStatus === 'Delivered').length;
    const openedEmails = emails.filter(email => email.emailStatus === 'Opened').length;
    const repliedEmails = emails.filter(email => email.emailStatus === 'Replied').length;
    const bouncedEmails = emails.filter(email => email.emailStatus === 'Bounced').length;

    return {
      totalEmails,
      sentEmails,
      deliveredEmails,
      openedEmails,
      repliedEmails,
      bouncedEmails,
      deliveryRate: sentEmails > 0 ? (deliveredEmails / sentEmails * 100).toFixed(2) : 0,
      openRate: deliveredEmails > 0 ? (openedEmails / deliveredEmails * 100).toFixed(2) : 0,
      replyRate: openedEmails > 0 ? (repliedEmails / openedEmails * 100).toFixed(2) : 0,
      bounceRate: sentEmails > 0 ? (bouncedEmails / sentEmails * 100).toFixed(2) : 0
    };
  }
};
